import React, { useMemo } from "react";
import { Line } from "react-konva";
import { KonvaEventObject } from "konva/lib/Node";
import { WithLineStyle, EPDStyleTracks } from "../core/styles";
import { TrackWithGeometry } from "@opstel/query/models/track";
import { LineString } from "@opstel/geo/LineString";
import { useEPDStyle } from "../contexts/EPDStyle/useEPDStyle";
import { SCALING_VECTOR } from "../core/layoutConstants";

type Props = {
  readonly track: TrackWithGeometry;
  readonly trackType?: keyof EPDStyleTracks;
  readonly trackLineWidth: number;
};

export function EPDTrack({
  track,
  trackType = "default",
  trackLineWidth,
}: Props): React.JSX.Element {
  const { style } = useEPDStyle();

  const [isPointerOver, setIsPointerOver] = React.useState(false);

  const trackLineStyles = useMemo(() => {
    const trackStyle = style.tracks[trackType];
    return (isArray(trackStyle) ? trackStyle : [trackStyle]).map((i, x, array) =>
      isPointerOver && x === array.length - 1
        ? {
            ...i,
            lineColor: style.tracks.hover.lineColor,
            lineWidth: style.tracks.hover.lineWidth,
          }
        : {
            ...i,
          },
    );
  }, [style.tracks, trackType, isPointerOver]);

  return (
    <>
      {
        // oxlint-disable-next-line react/jsx-key
        trackLineStyles.map((style) => (
          <TrackLine
            onPointerOut={() => setIsPointerOver(false)}
            onPointerOver={() => setIsPointerOver(true)}
            geometry={
              new LineString(
                track.coordinates.fromSourceToTarget.map((coord) => coord.scale(SCALING_VECTOR)),
              )
            }
            style={style}
            lineWidth={trackLineWidth}
          />
        ))
      }
    </>
  );
}

const TrackLine = ({
  onPointerOver,
  onPointerOut,
  style,
  geometry,
  lineWidth,
}: {
  readonly onPointerOut: (e: KonvaEventObject<PointerEvent>) => void;
  readonly onPointerOver: (e: KonvaEventObject<PointerEvent>) => void;
  readonly style: WithLineStyle;
  readonly geometry: LineString;
  readonly lineWidth: number;
}) => {
  return (
    <Line
      x={0}
      y={0}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
      points={geometry.toOrdinates()}
      stroke={style.lineColor}
      dash={style.lineStyle === "dashed" ? [8, 8] : undefined}
      strokeWidth={style.lineWidth * lineWidth}
    />
  );
};

function isArray(obj: unknown): obj is Array<unknown> {
  return Array.isArray(obj);
}
